import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import {FaGraduationCap, FaCertificate, FaTrophy, FaNewspaper, FaMedal} from 'react-icons/fa';
import {FiExternalLink} from 'react-icons/fi';

import {urlFor} from '../../client';
// @ts-ignore
import FetchSanityData from '../../functions/FetchSanityData.ts';
// @ts-ignore
import getFormatedDateLength from '../../functions/getFormatedDateLength.ts';

import './Credentials.scss';

const MONTH_LIST : Array<string> = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/* Sanity date fields arrive as "YYYY-MM-DD". Parsing those through `new Date`
   treats them as UTC midnight, so a visitor west of UTC would read 2025-01-01
   back as December 2024 - hence reading the parts literally instead.

   `yearOnly` exists because the awards are cited by year on the CV and nothing
   records the month; showing "January" for them would invent a fact. */
const formatAwarded = (value?: string, yearOnly?: boolean) => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');

  if(!parts)
    return null;

  const [, year, month] = parts;

  if(yearOnly)
    return year;

  return `${MONTH_LIST[Number(month) - 1]} ${year}`;
}

/* Most recent first, matching how the experience timeline reads.
   Sanity returns documents in no guaranteed order, so sort explicitly.
   Entries without a start date sink to the bottom rather than jumping to
   the top on an invalid Date. */
const byDateDesc = (field : string) => (a, b) => {
  const toTime = (value?: string) => {
    if(!value)
      return null;

    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
  };

  const timeA = toTime(a?.[field]);
  const timeB = toTime(b?.[field]);

  if(timeA === null && timeB === null) return 0;
  if(timeA === null) return 1;
  if(timeB === null) return -1;

  return timeB - timeA;
};

const SectionHeading = ({icon, title, accent}) => (
  <h2 className="credentials__heading">
    <span className="credentials__heading-icon">{icon}</span>
    {title} <span>{accent}</span>
  </h2>
);

/* Only awards carry a result. Rendering nothing when it is unset keeps an
   unlabelled award from silently reading as a win. */
const ResultBadge = ({result}) => {
  if(result === 'Won')
    return <span className="credential-card__result credential-card__result--won"><FaTrophy /> Won</span>;

  if(result === 'Nominated')
    return <span className="credential-card__result"><FaMedal /> Nominated</span>;

  return null;
}

/* Awards and certificates share a shape: title, issuer, blurb, verify link.
   Press mentions reuse it with the outlet as issuer and the article as link. */
const CredentialCard = ({item, index, yearOnly = false, linkLabel = "Verify credential"}) => (
  <motion.article
    className="credential-card"
    initial={{opacity: 0, y: 14}}
    whileInView={{opacity: 1, y: 0}}
    viewport={{once: true, amount: 0.2}}
    transition={{duration: 0.32, delay: Math.min(index, 5) * 0.05, ease: [0.2, 0.8, 0.2, 1]}}
  >
    {item.imgUrl &&
      <div className="credential-card__logo">
        <img src={urlFor(item.imgUrl).width(160).auto('format').url()} alt="" />
      </div>
    }

    <div className="credential-card__body">
      <h3 className="credential-card__title">{item.title}</h3>

      <ResultBadge result={item.result} />

      <p className="credential-card__meta">
        {item.issuer}
        {item.issuer && formatAwarded(item.dateReceived, yearOnly) ? ' · ' : ''}
        {formatAwarded(item.dateReceived, yearOnly)}
      </p>

      {item.description && <p className="credential-card__desc">{item.description}</p>}

      {item.credentialLink &&
        <a
          className="credential-card__verify"
          href={item.credentialLink}
          target="_blank"
          rel="noreferrer"
        >
          {linkLabel} <FiExternalLink />
        </a>
      }
    </div>
  </motion.article>
);

const Credentials : React.FC = () => {
  const [university, setUniversity] = useState<Array<any>>([]);
  const [certificates, setCertificates] = useState<Array<any>>([]);
  const [awards, setAwards] = useState<Array<any>>([]);
  const [press, setPress] = useState<Array<any>>([]);

  useEffect(() => {
    FetchSanityData("university", (data) => setUniversity([...data].sort(byDateDesc("startingDate"))));
    FetchSanityData("certificates", (data) => setCertificates([...data].sort(byDateDesc("dateReceived"))));
    FetchSanityData("awards", (data) => setAwards([...data].sort(byDateDesc("dateReceived"))));
    FetchSanityData("press", (data) => setPress(
      [...data]
        .sort(byDateDesc("datePublished"))
        .map((item) => ({...item, issuer: item.outlet, dateReceived: item.datePublished, credentialLink: item.articleLink}))
    ));
  }, []);

  const hasNothing =
    university.length === 0 && certificates.length === 0 && awards.length === 0 && press.length === 0;

  return (
    <div className="credentials">
      <div className="credentials__inner">
        <header className="credentials__hero">
          <h1 className="credentials__title">My <span>Credentials</span></h1>
          <p className="credentials__lead">
            Education, certifications, awards, and press — with links to verify each one.
          </p>
        </header>

        {hasNothing &&
          <p className="credentials__empty">Nothing here yet. Add entries in the Sanity studio.</p>
        }

        {university.length > 0 &&
          <section className="credentials__section">
            <SectionHeading icon={<FaGraduationCap />} title="" accent="Education" />

            <div className="credentials-timeline">
              {university.map((entry, index) => {
                const {start, final, duration} = getFormatedDateLength(entry.startingDate, entry.leavingDate);

                return (
                  <motion.article
                    className="credentials-timeline__item"
                    key={`${entry.name}-${index}`}
                    initial={{opacity: 0, y: 14}}
                    whileInView={{opacity: 1, y: 0}}
                    viewport={{once: true, amount: 0.2}}
                    transition={{duration: 0.32, ease: [0.2, 0.8, 0.2, 1]}}
                  >
                    {entry.imgUrl &&
                      <div className="credentials-timeline__logo">
                        <img src={urlFor(entry.imgUrl).width(160).auto('format').url()} alt="" />
                      </div>
                    }

                    <div className="credentials-timeline__body">
                      {entry.startingDate &&
                        <p className="credentials-timeline__date">{start} – {final} · {duration}</p>
                      }
                      <h3 className="credentials-timeline__name">{entry.name}</h3>
                      {entry.institution && <p className="credentials-timeline__institution">{entry.institution}</p>}
                      {entry.description && <p className="credentials-timeline__desc">{entry.description}</p>}
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </section>
        }

        {certificates.length > 0 &&
          <section className="credentials__section">
            <SectionHeading icon={<FaCertificate />} title="" accent="Certificates" />
            <div className="credentials__grid">
              {certificates.map((item, index) =>
                <CredentialCard item={item} index={index} key={`cert-${item.title}-${index}`} />
              )}
            </div>
          </section>
        }

        {awards.length > 0 &&
          <section className="credentials__section">
            <SectionHeading icon={<FaTrophy />} title="" accent="Awards/Nominations" />
            <div className="credentials__grid">
              {awards.map((item, index) =>
                <CredentialCard item={item} index={index} yearOnly key={`award-${item.title}-${index}`} />
              )}
            </div>
          </section>
        }

        {press.length > 0 &&
          <section className="credentials__section">
            <SectionHeading icon={<FaNewspaper />} title="In the" accent="Press" />
            <div className="credentials__grid">
              {press.map((item, index) =>
                <CredentialCard item={item} index={index} linkLabel="Read article" key={`press-${item.title}-${index}`} />
              )}
            </div>
          </section>
        }
      </div>
    </div>
  );
}

export default Credentials;
